import React, { PropsWithChildren, useContext, ReactElement } from "react";
import { List, Record } from "immutable";
import { FormContext } from "common/components/Form/FormContext";
import { parsePath } from "common/helpers/FormPath";
import { useDispatch } from "react-redux";
import Select, { InputActionMeta, Props } from "react-select";
import { useValidate } from "common/components/Form/hooks/useValidate";
import { DropdownRecord, IDropdownData, fromJSON } from "common/components/Dropdown/Dropdown";
import {
  classNames,
  dropdownCustomComponents,
  labelClasses,
  mergeStyles,
} from "common/components/Dropdown/DropdownStyles";
import { DropdownMenuPortal } from "common/components/Dropdown/DropdownMenuPortal";
import { IdType, Nullable } from "common/types";
import { P } from "common/components/Paragraph";
import { debounce } from "common/helpers/Async";
import { useAppSelector, useAppTracking } from "common/hooks/storeHooks";

export interface IDropdownDataProps {
  id?: string;
  label?: string;
  name: string;
  disabled: boolean;
  options: List<Record<IDropdownData>>;
  validate?: (value: DropdownRecord | List<DropdownRecord>) => Nullable<string>;
  onQueryInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  onValueChange?: (selected: List<DropdownRecord>) => void;
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  selectProps?: Partial<Props>;
  placeholder?: string;
  isLoading: boolean;
  allowCustomValue?: boolean;
}

/**
 *  DropdownData component
 */
export const DropdownData = ({
  id,
  label,
  name,
  disabled,
  options,
  validate,
  onQueryInputChange,
  onValueChange,
  isMulti,
  closeMenuOnSelect,
  selectProps,
  isLoading,
  placeholder,
  allowCustomValue,
}: PropsWithChildren<IDropdownDataProps>): ReactElement<any> => {
  // Hooks
  const dispatch = useDispatch();
  const { trackEvent } = useAppTracking();
  const { rootPath, updateFormReducer, errors } = useContext(FormContext);
  const idName = id || name;
  useValidate(validate, name);

  // Selectors
  const path = parsePath(name);
  const reduxValue: (string | number) | List<string | number> = useAppSelector(
    (state) => state.getIn(rootPath.concat(path)) as any,
  );
  const selected = isMulti
    ? (reduxValue as List<Nullable<IdType>>)?.size > 0
      ? options.filter((option) => (reduxValue as List<Nullable<IdType | boolean>>).includes(option?.get("id")))?.toJS()
      : ""
    : allowCustomValue
      ? fromJSON({ id: reduxValue as string | number, name: reduxValue?.toString() })
      : options.find((option) => option?.get("id") === reduxValue) || "";
  const optionsWithCustomValue = allowCustomValue
    ? (() => {
        const notFound = options.every((option) => option?.get("id") !== reduxValue);
        return notFound
          ? options.unshift(fromJSON({ id: reduxValue as string | number, name: reduxValue?.toString() }))
          : options;
      })()
    : options;

  // Methods
  const handleOnChange = (newValue: unknown) => {
    const items = newValue as IDropdownData | IDropdownData[];
    const emptyArray = [] as IDropdownData[];
    const wrappedItems = emptyArray.concat(items);
    const records = List(wrappedItems.map((item) => fromJSON(item)));
    const value = isMulti ? records.map((r) => r.id) : records.first()?.id;
    const valueString = records.map((r) => r.id).join(", ");

    dispatch(updateFormReducer({ rootPath, name, value }));
    trackEvent({
      action: "dropdown selection changed",
      value: valueString,
      isMulti: isMulti || false,
    });
    if (onValueChange) onValueChange(records);
  };

  const customValueQueryChange = debounce((inputValue: string) => {
    if (inputValue === "" || !allowCustomValue) return;
    const customOption = { id: inputValue, name: inputValue };

    handleOnChange(customOption);
  }, 500);

  return (
    <div className="flex flex-col gap-xs w-full">
      {label && (
        <label htmlFor={idName} className="flex items-center h-[20px]">
          <P size="sm" className={labelClasses}>
            {label}
          </P>
        </label>
      )}
      <Select
        inputId={idName}
        onChange={handleOnChange}
        name={name}
        value={selected}
        menuPortalTarget={document.getElementById("dropdown-portal")}
        options={optionsWithCustomValue?.toJS() || []}
        placeholder={placeholder}
        isDisabled={disabled}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        getOptionLabel={(x: unknown) => (x as IDropdownData).name}
        getOptionValue={(x: unknown) => (x as IDropdownData).id?.toString() || ""}
        onInputChange={allowCustomValue ? customValueQueryChange : onQueryInputChange}
        filterOption={allowCustomValue ? () => true : undefined}
        isLoading={isLoading}
        classNames={classNames}
        components={{ ...dropdownCustomComponents, MenuPortal: DropdownMenuPortal }}
        // Kept so react-select's MenuPlacer still treats the menu as fixed (skips its scroll-into-view path).
        menuPosition="fixed"
        hideSelectedOptions={false}
        {...{ ...selectProps, hasError: errors.get(name) }}
        styles={mergeStyles(selectProps?.styles)}
      />
      {errors.get(name) && (
        <P className="text-error-400 leading-[16px]" size="sm">
          {errors.get(name)}
        </P>
      )}
    </div>
  );
};
